import { useContext, useEffect, useState } from "react"
import { GanttContext, GanttScale } from "./Gantt";
import { format } from "date-fns";
import { dateformat } from "@/utils/GlobalConstants";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/Components/ui/dropdown-menu";
import { CalendarRangeIcon } from "@/Components/ui/Icons/TooltipIcon";

const GanttControls = () => {
  const ganttProps = useContext(GanttContext);

  useEffect(() => {
    if (ganttProps.ganttScale === "day") {
      setMaxDisplayLength(30);
    }
    if (ganttProps.ganttScale === "week") {
      setMaxDisplayLength(16);
    }
    if (ganttProps.ganttScale === "month") {
      setMaxDisplayLength(12);
    }
    if (ganttProps.ganttScale === "year") {
      setMaxDisplayLength(10);
    }
  }, [ganttProps.ganttScale]);

  const scaleOptions: { label: string; value: GanttScale }[] = [
    { label: "Day", value: "day" },
    { label: "Week", value: "week" },
    { label: "Month", value: "month" },
    { label: "Year", value: "year" },
  ];

  const minDisplayLength = 1;
  const [maxDisplayLength, setMaxDisplayLength] = useState<number>(getMaxDisplayLength());

  // Calculate the number of units (days/weeks/months/years) between minDate and maxDate
  function getUnitStep() {
    switch (ganttProps.ganttScale) {
      case "day": return 1;
      case "week": return 7;
      case "month": return 30;
      case "year": return 365;
      default: return 1;
    }
  };

  function getMaxDisplayLength() {
    switch (ganttProps.ganttScale) {
      case "day": return 60; // 60 days
      case "week": return 16; // 16 weeks
      case "month": return 12; // 12 months
      case "year": return 10; // 10 years
      default: return 60; // Default to 60 days if scale is not recognized
    }
  };

  // Handle scale slider
  const handleScaleChange = (scale: GanttScale) => {
    ganttProps.setGanttScale(scale);
    if (scale === "day") {
      ganttProps.setDisplayLength(21);
      setMaxDisplayLength(30);
    }
    if (scale === "week") {
      ganttProps.setDisplayLength(4);
      setMaxDisplayLength(16);
    }
    if (scale === "month") {
      ganttProps.setDisplayLength(3);
      setMaxDisplayLength(12);
    }
    if (scale === "year") {
      ganttProps.setDisplayLength(3);
      setMaxDisplayLength(10);
    }
  };

  // Handle display length slider
  const handleDisplayLengthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    ganttProps.setDisplayLength(Number(e.target.value));
  };

  // Handle start date slider
  const handleStartDateSlider = (e: React.ChangeEvent<HTMLInputElement>) => {
    const units = Number(e.target.value);
    const newDate = new Date(ganttProps.projectStartDate);
    newDate.setDate(ganttProps.projectStartDate.getDate() + units * getUnitStep());
    ganttProps.setDisplayStartDate(newDate);
  };

  // Calculate the number of units (days/weeks/months/years) between minDate and the latest valid start date
  function getStartDateSliderMax() {
    const unitStep = getUnitStep();
    const unitMs = unitStep * 24 * 60 * 60 * 1000;
    const lastValidStart = new Date(ganttProps.projectEndDate);
    const diffMs = lastValidStart.getTime() - ganttProps.projectStartDate.getTime();

    if (unitMs >= diffMs && diffMs > 0) {
      return 1;
    }

    lastValidStart.setDate(lastValidStart.getDate() - (ganttProps.displayLength - 1) * unitStep);

    return Math.max(0, Math.ceil(diffMs / unitMs));
  }

  function getStartDateSliderValue() {
    const dateDiff = new Date(ganttProps.displayStartDate).getTime() - new Date(ganttProps.projectStartDate).getTime();
    const unitStep = getUnitStep() * 24 * 60 * 60 * 1000;

    if (unitStep >= dateDiff) {
      return new Date(ganttProps.displayStartDate).getTime() > new Date(ganttProps.projectStartDate).getTime() ? 1 : 0;
    }

    return Math.round(
      (new Date(ganttProps.displayStartDate).getTime() - new Date(ganttProps.projectStartDate).getTime()) /
      (getUnitStep() * 24 * 60 * 60 * 1000)
    );
  }

  return (
    <div className="flex flex-row justify-between border-2 border-gray-500 w-full rounded-md p-4 mb-2">
      {/* Scale Slider */}
      <div className="flex items-center gap-4">
        <span>Scale:</span>
        <DropdownMenu /* open={openMenu} onOpenChange={setOpenMenu} modal={false} */>
          <DropdownMenuTrigger asChild>
            <button id="select-time-frame-button" className="button-default flex flex-row"><CalendarRangeIcon tooltip="Select Scale" className="mr-2" />{scaleOptions.find(option => option.value === ganttProps.ganttScale)?.label}</button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <div className="flex flex-col space-y-2 p-2">
              <button className={ganttProps.ganttScale === 'day' ? "bg-accent text-white" : ""} onClick={() => {
                handleScaleChange('day');
              }}>Day</button>
              <button className={ganttProps.ganttScale === 'week' ? "bg-accent text-white" : ""} onClick={() => {
                handleScaleChange('week');
              }}>Week</button>
              <button className={ganttProps.ganttScale === 'month' ? "bg-accent text-white" : ""} onClick={() => {
                handleScaleChange('month');
              }}>Month</button>
              {/*<button className={ganttProps.ganttScale === 'quarter' ? "bg-accent text-white" : ""} onClick={() => {
                handleScaleChange('quarter'); // TO DO - implement quarter scale
              }}>Quarter</button>*/}
              <button className={ganttProps.ganttScale === 'year' ? "bg-accent text-white" : ""} onClick={() => {
                handleScaleChange('year');
              }}>Year</button>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {/* Display Length Slider */}
      <div className="flex items-center gap-4">
        <span>Display Length: <span className="ml-2 font-semibold">{ganttProps.displayLength}</span></span>
        <input
          type="range"
          min={minDisplayLength}
          max={maxDisplayLength}
          value={ganttProps.displayLength}
          onChange={handleDisplayLengthChange}
          className="w-64 accent-accent"
        />

      </div>
      {/* Start Date Slider */}
      <div className="flex items-center gap-4">
        <span>View From: <span className="ml-2 font-semibold">{format(new Date(ganttProps.displayStartDate), dateformat)}</span></span>
        <input
          type="range"
          min={0}
          max={getStartDateSliderMax()}
          step={1}
          value={getStartDateSliderValue()}
          onChange={handleStartDateSlider}
          className="w-64 accent-accent"
        />
      </div>
      <button className="button-default" onClick={ganttProps.scaleToSelected}>Scale to Selected</button>
    </div>
  );
};

export default GanttControls;